import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { cn } from '@/lib/utils';
import type { CloneStep } from '@/modules/templates/services/templates.service';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CloneTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title of the template being cloned — used to seed the default copy name. */
  sourceTitle: string;
  /** True while the clone mutation is in flight. */
  isCloning: boolean;
  /** Current clone phase, or null before it starts — drives the progress view. */
  currentStep: CloneStep | null;
  /** Performs the clone. Rejects on failure (error handled by the caller). */
  onClone: (newTitle: string) => Promise<void>;
}

/** Ordered phases shown in the progress view (excludes the transient "done"). */
const PROGRESS_STEPS: { key: CloneStep; labelKey: string }[] = [
  { key: 'preparing', labelKey: 'ORGTEMPLATES_CLONE_STEP_PREPARING' },
  { key: 'creating', labelKey: 'ORGTEMPLATES_CLONE_STEP_CREATING' },
  { key: 'copying', labelKey: 'ORGTEMPLATES_CLONE_STEP_COPYING' },
];

const STEP_ORDER: CloneStep[] = ['preparing', 'creating', 'copying', 'done'];

/**
 * Asks the user for the copied template's name and confirmation, then shows a
 * multi-step progress loader while the deep clone runs.
 */
const CloneTemplateDialog = ({
  open,
  onOpenChange,
  sourceTitle,
  isCloning,
  currentStep,
  onClone,
}: CloneTemplateDialogProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');

  // Seed with "<source> (Copy)" each time the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle(`${sourceTitle} (Copy)`);
    }
  }, [open, sourceTitle]);

  const trimmed = title.trim();
  const canClone = trimmed.length > 0 && !isCloning;

  const handleOpenChange = (next: boolean) => {
    if (isCloning) return;
    onOpenChange(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canClone) return;
    try {
      await onClone(trimmed);
      onOpenChange(false);
    } catch {
      // Error is handled by the caller (toast/handleError).
    }
  };

  const currentIndex = currentStep ? STEP_ORDER.indexOf(currentStep) : -1;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" hideClose={isCloning}>
        <DialogHeader>
          <DialogTitle>{t('ORGTEMPLATES_CLONE_DIALOG_TITLE')}</DialogTitle>
          <DialogDescription>
            {isCloning
              ? t('ORGTEMPLATES_CLONE_PROGRESS_DESCRIPTION', { title: sourceTitle })
              : t('ORGTEMPLATES_CLONE_DIALOG_DESCRIPTION', { title: sourceTitle })}
          </DialogDescription>
        </DialogHeader>

        {isCloning ? (
          <ul className="space-y-3 py-2">
            {PROGRESS_STEPS.map((step) => {
              const index = STEP_ORDER.indexOf(step.key);
              const isDone = currentIndex > index;
              const isActive = currentIndex === index;
              return (
                <li key={step.key} className="flex items-center gap-3">
                  <span
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full border',
                      isDone
                        ? 'border-lime-green bg-lime-green text-white'
                        : isActive
                          ? 'border-selise-blue text-selise-blue'
                          : 'border-border text-globe-grey'
                    )}
                  >
                    {isDone ? (
                      <Check className="size-3.5" />
                    ) : isActive ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <span className="size-1.5 rounded-full bg-globe-grey" />
                    )}
                  </span>
                  <span
                    className={cn(
                      'text-sm',
                      isDone || isActive
                        ? 'font-medium text-[hsl(var(--fg))]'
                        : 'text-globe-grey'
                    )}
                  >
                    {t(step.labelKey)}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clone-template-input">
                {t('ORGTEMPLATES_CLONE_DIALOG_NAMELABEL')}
              </Label>
              <Input
                id="clone-template-input"
                value={title}
                autoFocus
                maxLength={200}
                placeholder={t('ORGTEMPLATES_CLONE_DIALOG_NAMEPLACEHOLDER')}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('CANCEL')}
              </Button>
              <Button type="submit" variant="primary" disabled={!canClone}>
                {t('ORGTEMPLATES_ACTION_CLONE')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CloneTemplateDialog;
